import React, { useCallback, useEffect, useMemo } from "react";
import { useFormik, FormikProvider } from "formik";
import Grid from "@mui/material/Grid";
import { Box, Button, Divider, Typography } from "@mui/material";

import RRDDirectPrintTypeInput from "../common/rrdInputFields/RRDDirectPrintTypeInput";
import RRDDirectMailingDateInput from "../common/rrdInputFields/RRDDirectMailingDateInput";
import RRDDirectMarketInput from "../common/rrdInputFields/RRDDirectMarketInput";
import RRDDirectMailToolTips from "../common/RRDDirectMailToolTips";
import { logger } from "@/helpers/loggerHelper";
import RRDDirectDistributionToggleInput from "../common/rrdInputFields/RRDDirectDistributionToggleInput";
import RRDDirectStoreCopyToggleInput from "../common/rrdInputFields/RRDDirectStoreCopyToggleInput";
import RRDDirectStoreQntInput from "../common/rrdInputFields/RRDDirectStoreQntInput";
import RRDDirectDifferentExpToggleInput from "../common/rrdInputFields/RRDDirectDifferentExpToggleInput";
import RRDDirectFrontCouponExpInput from "../common/rrdInputFields/RRDDirectFrontCouponExpInput";
import RRDDirectBackCouponExpInput from "../common/rrdInputFields/RRDDirectBackCouponExpInput";
import RRDTemplate from "../common/rrdTemplate/RRDTemplate";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";
import {
  doRRDPrintTypesMatch,
  supportsRRDDifferentExpiry,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import RRDMailingDateAvailabilityDays from "../common/rrdInputFields/RRDMailingDateAvailabilityDays";
import {
  getRRDStoreCopyFormFields,
  resolveRRDStoreCopyConfig,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";

type RRDRecord = Record<string, any>;

type RRDDirectBulkSelectionFormProps = {
  onSubmitBulkSelection?: (_values: RRDRecord) => Promise<any> | any;
  minCouponDurationAdmin?: any;
  lastCouponExpiryDateAdmin?: any;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
  onChangeBulkSelection?: (_values: RRDRecord) => void;
  marketList?: RRDRecord[];
  locations?: RRDRecord[];
  checkedLocations?: any[];
  disableLocations?: any[];
  campaignData?: RRDRecord;
};

const DifferentExpiryToggleInput =
  RRDDirectDifferentExpToggleInput as React.ElementType;
const StoreCopyToggleInput = RRDDirectStoreCopyToggleInput as React.ElementType;

function RRDDirectBulkSelectionForm({
  onSubmitBulkSelection,
  minCouponDurationAdmin,
  lastCouponExpiryDateAdmin,
  couponExpirationSetBy,
  fixedCouponDurationDays,
  onChangeBulkSelection,
  marketList,
  locations,
  checkedLocations = [],
  campaignData,
}: RRDDirectBulkSelectionFormProps) {
  const getBulkLocation = useCallback(
    (selectionValues: RRDRecord) => {
      return locations?.find((location) => {
        return (
          (!selectionValues.print_type ||
            doRRDPrintTypesMatch(
              location.print_type,
              selectionValues.print_type
            )) &&
          (!selectionValues.market ||
            location.market_name === selectionValues.market)
        );
      });
    },
    [locations]
  );

  const getBulkLayoutKey = useCallback(
    (selectionValues: RRDRecord) => {
      const matchingLocation = getBulkLocation(selectionValues);
      return (
        matchingLocation?.layout_key || matchingLocation?.layoutKey || null
      );
    },
    [getBulkLocation]
  );

  const initialValues = useMemo(() => {
    return {
      print_type: "",
      layout_key: "",
      market: "",
      mailing_date: "",

      hero_images: [],
      coupons: [],

      distribution: false,

      store_copy: false,
      store_copy_qnt: "",

      different_coupon_expiry: false,
      front_coupon_expiry_date: "",
      back_coupon_expiry_date: "",
      couponExpirationSetBy,
      fixedCouponDurationDays,
    };
  }, [couponExpirationSetBy, fixedCouponDurationDays]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (shouldBlockUnsafeSubmission(values)) {
          setSubmitting(false);
          return;
        }

        if (typeof onSubmitBulkSelection === "function") {
          const storeCopyConfig = resolveRRDStoreCopyConfig({
            location: getBulkLocation(values),
            campaign: campaignData,
          });
          const result = await onSubmitBulkSelection({
            ...values,
            ...getRRDStoreCopyFormFields(storeCopyConfig),
            layout_key: values.layout_key || getBulkLayoutKey(values),
          });
          // Only reset form if onSubmit returns true (successful submission)
          if (result === true || result?.success === true) {
            resetForm();
          }
        }
      } catch (error) {
        logger.error("Form submission error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { isSubmitting, values } = formik;

  const allMailingWeeks = useMemo(() => {
    const mailingWeeks = (locations || []).reduce<any[]>((acc, l) => {
      if (
        doRRDPrintTypesMatch(l.print_type, values.print_type) &&
        l.market_name === values.market
      ) {
        return [...acc, ...l.mailing_weeks];
      }
      return acc;
    }, []);
    return [...new Set(mailingWeeks)];
  }, [locations, values.print_type, values.market]);

  const allAssetsList = useMemo(() => {
    return rrdDirectMailCampaignHelper.getAllAssets(
      locations,
      values.print_type,
      values.market
    );
  }, [locations, values.print_type, values.market]);

  const bulkLayoutKey = useMemo(
    () => getBulkLayoutKey(values),
    [getBulkLayoutKey, values]
  );
  const bulkStoreCopyConfig = useMemo(() => {
    return resolveRRDStoreCopyConfig({
      location: getBulkLocation(values),
      campaign: campaignData,
    });
  }, [campaignData, getBulkLocation, values]);

  const bulkSelectionAvailabilityData = useMemo(() => {
    return rrdDirectMailCampaignHelper.getWeeklyAvailabilityData(
      locations,
      checkedLocations,
      allMailingWeeks,
      values.print_type,
      values.market
    );
  }, [
    locations,
    checkedLocations,
    values.print_type,
    values.market,
    allMailingWeeks,
  ]);

  const daySlot = useMemo(
    () => (props) => (
      <RRDMailingDateAvailabilityDays
        {...props}
        weeklyAvailabilityData={bulkSelectionAvailabilityData}
      />
    ),
    [bulkSelectionAvailabilityData]
  );

  useEffect(() => {
    onChangeBulkSelection?.(values);
  }, [values, onChangeBulkSelection]);

  useEffect(() => {
    // reset form values when print type changes
    formik.setValues((prevValues) => ({
      ...initialValues,
      print_type: prevValues.print_type,
    }));
  }, [values.print_type, initialValues]);

  useEffect(() => {
    // reset some form values when market changes
    formik.setValues((prevValues) => ({
      ...initialValues,
      print_type: prevValues.print_type,
      market: prevValues.market,
    }));
  }, [values.market, initialValues]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched
    await formik.setTouched({
      print_type: true,
      market: true,
      mailing_date: true,
      hero_images: true,
      coupons: true,
      distribution: true,
      store_copy: true,
      store_copy_qnt: true,
      different_coupon_expiry: true,
      front_coupon_expiry_date: true,
      back_coupon_expiry_date: true,
    });

    // Validate and submit
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      formik.submitForm();
    }
  };

  const displayMarketInput = useMemo(() => {
    return !!values.print_type;
  }, [values.print_type]);

  const displayAllInput = useMemo(() => {
    return !!values.print_type && !!values.market;
  }, [values.print_type, values.market]);

  const displayDifferentCouponExpiryInput = useMemo(() => {
    return supportsRRDDifferentExpiry(values.layout_key || bulkLayoutKey);
  }, [values.layout_key, bulkLayoutKey]);

  const expiryDateConstraints = useMemo(() => {
    return rrdDirectMailCampaignHelper.getExpiryDateConstraints(
      values?.mailing_date,
      minCouponDurationAdmin,
      lastCouponExpiryDateAdmin
    );
  }, [values?.mailing_date, minCouponDurationAdmin, lastCouponExpiryDateAdmin]);

  return (
    <div>
      <FormikProvider value={formik}>
        {/* We can not use form in side form so we have used useFormik with FormikProvider */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <Typography
                  variant="body2"
                  gutterBottom
                  fontWeight={600}
                  className="mb-2"
                >
                  Print Type{" "}
                  <RRDDirectMailToolTips
                    customContent={
                      <Box>
                        <Typography
                          variant="caption"
                          gutterBottom
                          component="p"
                        >
                          <strong>Value Sheet Insert (8.5” x11”)</strong> -
                          Printed flyer that is placed within our Save direct
                          mail package, along with other advertisers.
                        </Typography>
                        <Typography variant="caption" component="p">
                          <strong>Basic Postcard (8.375” x 5”)</strong> - Arrive
                          in the mailbox as a stand-alone postcard outside of
                          our Save direct mail package.
                        </Typography>
                      </Box>
                    }
                  />
                </Typography>
                <RRDDirectPrintTypeInput
                  name="print_type"
                  wrapperProps={{ className: "ml-0" }}
                />
              </Grid>
              {displayMarketInput && (
                <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                  <Typography
                    variant="body2"
                    gutterBottom
                    fontWeight={600}
                    className="mb-2"
                  >
                    Market <RRDDirectMailToolTips type="market" />
                  </Typography>
                  <RRDDirectMarketInput name="market" marketList={marketList} />
                </Grid>
              )}
              {displayAllInput && (
                <>
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                    <RRDDirectMailingDateInput
                      name="mailing_date"
                      mailingWeeks={allMailingWeeks}
                      daySlot={daySlot}
                      minCouponDurationAdmin={minCouponDurationAdmin}
                      lastCouponExpiryDateAdmin={lastCouponExpiryDateAdmin}
                      couponExpirationSetBy={couponExpirationSetBy}
                      fixedCouponDurationDays={fixedCouponDurationDays}
                      frontCouponExpiryDateFieldName={`front_coupon_expiry_date`}
                      backCouponExpiryDateFieldName={`back_coupon_expiry_date`}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 3 }}
                    sx={{
                      display: { xs: "none", xl: "flex" },
                      justifyContent: "flex-end",
                      alignItems: "start",
                      mt: 3,
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleFormSubmit}
                      disabled={isSubmitting}
                    >
                      Apply to selected locations
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          {displayAllInput && (
            <>
              <Grid size={{ xs: 12 }} className="mt-2">
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      ...(values?.different_coupon_expiry
                        ? { md: 6, lg: 4, xl: 3 }
                        : { md: 6, lg: 4, xl: 3 }),
                    }}
                  >
                    <RRDDirectFrontCouponExpInput
                      name="front_coupon_expiry_date"
                      containerClassName="rrd-coupon-expiry-input"
                      expiryDateConstraints={expiryDateConstraints}
                      minCouponDurationDays={minCouponDurationAdmin}
                      couponExpirationSetBy={couponExpirationSetBy}
                      fixedCouponDurationDays={fixedCouponDurationDays}
                    />
                  </Grid>

                  {couponExpirationSetBy !== "brand" &&
                  displayDifferentCouponExpiryInput ? (
                    <>
                      <Grid
                        size={{ xs: 12, md: 6, lg: 4, xl: 3 }}
                        sx={{
                          display: values?.different_coupon_expiry
                            ? "block"
                            : "none",
                        }}
                      >
                        <RRDDirectBackCouponExpInput
                          name="back_coupon_expiry_date"
                          containerClassName="rrd-coupon-expiry-input"
                          expiryDateConstraints={expiryDateConstraints}
                        />
                      </Grid>

                      <Grid
                        size={{
                          xs: 12,
                          ...(values?.different_coupon_expiry
                            ? { md: "auto", lg: 4 }
                            : { md: 6, lg: "auto" }),
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          mt: {
                            xs: 0,
                            ...(values?.different_coupon_expiry
                              ? { md: 0, lg: 3 }
                              : { md: 3 }),
                          },
                        }}
                      >
                        <DifferentExpiryToggleInput
                          differentCouponExpiryName="different_coupon_expiry"
                          backCouponExpiryFieldName="back_coupon_expiry_date"
                          expiryDateConstraints={expiryDateConstraints}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, lg: 4, xl: 3 }}>
                <RRDDirectDistributionToggleInput
                  name="distribution"
                  storeCopyToggleName="store_copy"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <StoreCopyToggleInput
                  name="store_copy"
                  disabled={!values.distribution}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <RRDDirectStoreQntInput
                  name="store_copy_qnt"
                  storeCopyToggleName="store_copy"
                  hideWhenStoreCopyIsOff
                  storeCopyConfig={bulkStoreCopyConfig}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Pro tip:</strong> if you want to make minor changes to
                  individual locations, first make bulk selections and then edit
                  individual locations.
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        {displayAllInput && (
          <>
            <Divider sx={{ my: 3 }} />
            <RRDTemplate
              heroImageFieldName="hero_images"
              couponFieldName="coupons"
              frontCouponExpirationDateFieldName="front_coupon_expiry_date"
              backCouponExpirationDateFieldName="back_coupon_expiry_date"
              printType={values.print_type}
              layoutKey={values.layout_key || bulkLayoutKey}
              assets={allAssetsList}
              isBulk={true}
            />
          </>
        )}

        {displayAllInput && (
          <>
            <Divider sx={{ my: 3, display: { xs: "flex", xl: "none" } }} />
            <Box
              sx={{
                display: { xs: "flex", xl: "none" },
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "start",
                mt: 3,
                mb: 2,
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                Apply to selected locations
              </Button>
            </Box>
          </>
        )}
      </FormikProvider>
    </div>
  );
}

export default RRDDirectBulkSelectionForm;
